import { StateCreator } from 'zustand';
import { Chess } from 'chess.js';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { GameState } from '../gameStore';

export interface GameSlice {
  game: Chess;
  isGameActive: boolean;
  isThinking: boolean;
  lastMove: { from: string; to: string } | null;
  isGameOver: () => boolean;
  
  resetGame: (startNewGame?: boolean) => void;
  makeMove: (from: string, to: string) => boolean;
  getAiMove: () => Promise<void>;
}

export const createGameSlice: StateCreator<
  GameState,
  [],
  [],
  GameSlice
> = (set, get) => {
  const makeAiMove = httpsCallable(functions, 'make_move');

  return {
    game: new Chess(),
    isGameActive: false,
    isThinking: false,
    lastMove: null,
    
    isGameOver: () => {
      return get().game.isGameOver() || get().timeOut !== null;
    },
    
    resetGame: (startNewGame = true) => {
      const { playerColor } = get();
      const { gameTimeLimit } = get();
      const timeInSeconds = gameTimeLimit * 60;
      let newOrientation = get().boardOrientation;
      
      if (playerColor === 'random') {
        newOrientation = Math.random() < 0.5 ? 'black' : 'white';
      }

      const initialActivePlayer = 
        (playerColor === 'black' || (playerColor === 'random' && newOrientation === 'black')) 
          ? 'model' 
          : 'player';
      
      set({ 
        game: new Chess(),
        isGameActive: startNewGame,
        lastMove: null,
        playerTimeRemaining: timeInSeconds,
        modelTimeRemaining: timeInSeconds,
        activePlayer: initialActivePlayer,
        timeOut: null,
        currentScore: 0,
        showHighScoreEntry: false,
        highScoreSubmitted: false
      });
      
      if (startNewGame) {
        set({boardOrientation: newOrientation})
        if (initialActivePlayer === 'model') {
          setTimeout(() => {
            get().getAiMove();
          }, 500);
        }
      }
    },
    
    makeMove: (from, to) => {
      const { game, isGameActive, boardOrientation, timeOut } = get();
      
      if (!isGameActive || timeOut !== null) return false;
      
      const isWhiteTurn = game.turn() === 'w';
      const playerIsWhite = boardOrientation === 'white';
      
      if ((isWhiteTurn && !playerIsWhite) || (!isWhiteTurn && playerIsWhite)) {
        return false;
      }
      
      try {
        const move = game.move({
          from,
          to,
          promotion: 'q'
        });
        
        if (move === null) return false;
        
        const newGame = new Chess(game.fen());
        
        set({ 
          game: newGame,
          lastMove: { from, to }
        });
        
        get().switchActivePlayer();
        
        if (!newGame.isGameOver()) {
          get().getAiMove();
        }
        
        return true;
      } catch (e) {
        return false;
      }
    },
    
    getAiMove: async () => {
      const { game, selectedModel, useVision, lastMove } = get();
      
      set({ isThinking: true });
      
      try {
        // Convert lastMove object to UCI format string if it exists
        const lastMoveUci = lastMove ? `${lastMove.from}${lastMove.to}` : null;

        const result = await makeAiMove({ 
          fen: game.fen(), 
          model: selectedModel,
          vision: useVision,
          last_move: lastMoveUci
        });
        
        const aiMove = (result.data as any).move;
        
        const fromSquare = aiMove.substring(0, 2);
        const toSquare = aiMove.substring(2, 4);
        
        game.move(aiMove);
        
        set({ 
          game: new Chess(game.fen()),
          lastMove: { from: fromSquare, to: toSquare }
        });
        
        get().switchActivePlayer();
        
      } catch (error) {
        console.error('Error getting AI move:', error);
      } finally {
        set({ isThinking: false });
      }
    },
  };
};